import { Box, Table, TableBody, TableCell, TableHead, TableRow, Theme } from '@mui/material';
import { Category } from '@synergy/models';
import { ConfirmationDialog, PoolFormattedMessage } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { getFilteredCategoryIds, getLocalizedString } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as CategoryActions from '../../../../common/actions/category-actions';
import { CategoryConfigurationRow } from './components/category-configuration-row';
import { localesSelector } from '../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        backgroundColor: theme.palette.background.paper,

        // No fixed row heights: the theme sets the density, the content the rest.
        // Only the first column keeps extra room for the tree indentation.
        '& th': {
            whiteSpace: 'nowrap',
            backgroundColor: theme.palette.background.paper,
        },
        '& th:first-child': {
            paddingLeft: theme.spacing(6),
        },
        '& td:last-child': {
            paddingRight: 8,
        },
    },
}));
interface CategoriesOverviewTableProps {
    onEdit: (item: Category, depth: number, unavailableEclasses: number[]) => void;
}

export const CategoriesOverviewTable = (props: CategoriesOverviewTableProps) => {
    const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
    const dispatch = useThunkDispatch();
    const { items: categories } = useAppSelector(state => state.categories);
    const [searchParams] = useSearchParams();
    const classes = useStyles();
    const locales = useAppSelector(localesSelector);

    useEffect(() => {
        dispatch(CategoryActions.fetchCategories());
    }, [dispatch]);

    const handleDelete = () => {
        if (categoryToDelete) {
            dispatch(CategoryActions.deleteCategory(categoryToDelete.id));
        }
        setCategoryToDelete(null);
    };

    const searchResult = getFilteredCategoryIds(categories, searchParams.get('search'));
    const topCategories = categories.filter(category => category.parentId === undefined);

    return (
        <Box sx={{ mx: theme => theme.general.pageContentSpacing, my: 2, flex: 1, overflow: 'auto' }}>
            <Table className={classes.table} stickyHeader={true}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id={'app.table.column.name'} />
                        </TableCell>
                        <TableCell>
                            <FormattedMessage id={'app.table.column.eclass'} />
                        </TableCell>
                        <TableCell>
                            {/* Needs a wrapper box because text alignment is overridden by the theme */}
                            <Box sx={{ textAlign: 'center' }}>
                                <FormattedMessage id={'app.table.column.showInShop'} />
                            </Box>
                        </TableCell>
                        <TableCell width={'1%'}>
                            <FormattedMessage id={'app.table.column.translation'} />
                        </TableCell>
                        <TableCell width={'1%'} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {topCategories.map(category => (
                        <CategoryConfigurationRow
                            key={category.id}
                            categories={categories}
                            category={category}
                            filterIds={searchResult}
                            depth={0}
                            unavailableEclasses={[]}
                            onEdit={props.onEdit}
                            onDelete={item => setCategoryToDelete(item)}
                        />
                    ))}
                </TableBody>
            </Table>

            <ConfirmationDialog
                isVisible={!!categoryToDelete}
                title={<FormattedMessage id="app.configuration.deleteCategoryModal.title" />}
                onCancel={() => setCategoryToDelete(null)}
                onConfirm={handleDelete}
                action={<FormattedMessage id="app.common.delete" />}
            >
                <PoolFormattedMessage
                    id="app.configuration.deleteCategoryModal.content"
                    values={{ name: getLocalizedString(locales, categoryToDelete?.translations) }}
                />
            </ConfirmationDialog>
        </Box>
    );
};
